import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import Navbar from '../components/Navbar';
import './Heroes.css';

function Heroes() {
  const [people, setPeople] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeCategory, setActiveCategory] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [showSuggestModal, setShowSuggestModal] = useState(false);
  const navigate = useNavigate();

  const categories = [
    { value: 'all', label: 'All' },
    { value: 'politics', label: 'Politics' },
    { value: 'arts', label: 'Arts' },
    { value: 'science', label: 'Science' },
    { value: 'sports', label: 'Sports' },
    { value: 'business', label: 'Business' },
    { value: 'social_reform', label: 'Social Reform' },
    { value: 'literature', label: 'Literature' },
    { value: 'education', label: 'Education' }
  ];

  useEffect(() => {
    fetchPeople();
  }, [activeCategory, searchQuery]);

  const fetchPeople = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('accessToken');
      const params = new URLSearchParams();
      if (activeCategory !== 'all') params.append('category', activeCategory);
      if (searchQuery) params.append('search', searchQuery);

      const response = await axios.get(
        `/api/famous-people?${params}`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      setPeople(response.data);
    } catch (error) {
      console.error('Error fetching famous people:', error);
    } finally {
      setLoading(false);
    }
  };

  const getCategoryColor = (category) => {
    const colors = {
      politics: '#3b82f6',
      arts: '#8b5cf6',
      science: '#10b981',
      sports: '#f59e0b',
      business: '#ef4444',
      social_reform: '#ec4899',
      literature: '#6366f1',
      education: '#14b8a6'
    };
    return colors[category] || '#6b7280';
  };

  const formatYears = (birthYear, deathYear) => {
    if (!birthYear) return '';
    if (deathYear) return `${birthYear} - ${deathYear}`;
    return `${birthYear} - Present`;
  };

  const truncateBio = (bio, maxLength = 150) => {
    if (bio.length <= maxLength) return bio;
    return bio.substring(0, maxLength) + '...';
  };

  return (
    <div>
      <Navbar />

      <div className="heroes-page">
        <div className="heroes-header">
          <div>
            <h1>Community Heroes</h1>
            <p>Celebrating notable figures from our community</p>
          </div>
          <button className="suggest-btn" onClick={() => setShowSuggestModal(true)}>
            ✨ Suggest Someone
          </button>
        </div>

        <div className="search-bar">
          <input
            type="text"
            placeholder="Search by name..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>

        <div className="category-tabs">
          {categories.map(cat => (
            <button
              key={cat.value}
              className={activeCategory === cat.value ? 'active' : ''}
              onClick={() => setActiveCategory(cat.value)}
            >
              {cat.label}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="loading">Loading heroes...</div>
        ) : people.length === 0 ? (
          <div className="no-people">
            <p>No heroes found. Be the first to suggest someone!</p>
          </div>
        ) : (
          <div className="people-grid">
            {people.map(person => (
              <div
                key={person.person_id}
                className="person-card"
                onClick={() => navigate(`/heroes/${person.person_id}`)}
              >
                <div className="person-photo">
                  {person.photo_url ? (
                    <img src={person.photo_url} alt={person.name} />
                  ) : (
                    <div className="photo-placeholder">
                      {person.name.charAt(0)}
                    </div>
                  )}
                </div>
                <div className="person-info">
                  <h3>{person.name}</h3>
                  {person.title && <p className="person-title">{person.title}</p>}
                  <p className="person-years">{formatYears(person.birth_year, person.death_year)}</p>
                  <span
                    className="category-badge"
                    style={{ backgroundColor: getCategoryColor(person.category) }}
                  >
                    {person.category.replace('_', ' ')}
                  </span>
                  <p className="person-bio">{truncateBio(person.biography)}</p>
                </div>
              </div>
            ))}
          </div>
        )}

        {showSuggestModal && (
          <SuggestModal
            onClose={() => setShowSuggestModal(false)}
            onSuccess={() => {
              setShowSuggestModal(false);
              alert('Thank you for your suggestion! It will be reviewed by our team.');
            }}
          />
        )}
      </div>
    </div>
  );
}

function SuggestModal({ onClose, onSuccess }) {
  const [formData, setFormData] = useState({
    name: '',
    title: '',
    biography: '',
    photo_url: '',
    birth_year: '',
    death_year: '',
    category: 'politics',
    achievements: '',
    notable_quote: ''
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('accessToken');
      await axios.post(
        '/api/famous-people/suggest',
        formData,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      onSuccess();
    } catch (error) {
      console.error('Error submitting suggestion:', error);
      alert(error.response?.data?.error || 'Failed to submit suggestion');
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content suggest-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Suggest a Community Hero</h2>
          <button className="close-btn" onClick={onClose}>×</button>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label>Name *</label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              required
            />
          </div>
          <div className="form-group">
            <label>Title</label>
            <input
              type="text"
              placeholder="e.g., Freedom Fighter, Scientist, Artist"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
            />
          </div>
          <div className="form-group">
            <label>Biography * (minimum 100 characters)</label>
            <textarea
              value={formData.biography}
              onChange={(e) => setFormData({ ...formData, biography: e.target.value })}
              rows="6"
              minLength="100"
              required
            />
            <div className="char-count">{formData.biography.length} characters</div>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label>Birth Year</label>
              <input
                type="number"
                min="1800"
                max={new Date().getFullYear()}
                value={formData.birth_year}
                onChange={(e) => setFormData({ ...formData, birth_year: e.target.value })}
              />
            </div>
            <div className="form-group">
              <label>Death Year (if applicable)</label>
              <input
                type="number"
                min="1800"
                max={new Date().getFullYear()}
                value={formData.death_year}
                onChange={(e) => setFormData({ ...formData, death_year: e.target.value })}
              />
            </div>
          </div>
          <div className="form-group">
            <label>Category *</label>
            <select
              value={formData.category}
              onChange={(e) => setFormData({ ...formData, category: e.target.value })}
              required
            >
              <option value="politics">Politics</option>
              <option value="arts">Arts</option>
              <option value="science">Science</option>
              <option value="sports">Sports</option>
              <option value="business">Business</option>
              <option value="social_reform">Social Reform</option>
              <option value="literature">Literature</option>
              <option value="education">Education</option>
            </select>
          </div>
          <div className="form-group">
            <label>Photo URL</label>
            <input
              type="url"
              placeholder="https://example.com/photo.jpg"
              value={formData.photo_url}
              onChange={(e) => setFormData({ ...formData, photo_url: e.target.value })}
            />
          </div>
          <div className="form-group">
            <label>Major Achievements (one per line)</label>
            <textarea
              value={formData.achievements}
              onChange={(e) => setFormData({ ...formData, achievements: e.target.value })}
              rows="4"
              placeholder="First achievement&#10;Second achievement&#10;Third achievement"
            />
          </div>
          <div className="form-group">
            <label>Notable Quote</label>
            <textarea
              value={formData.notable_quote}
              onChange={(e) => setFormData({ ...formData, notable_quote: e.target.value })}
              rows="2"
            />
          </div>
          <div className="form-note">
            <p>📝 Your suggestion will be reviewed by our team before being published.</p>
          </div>
          <div className="modal-actions">
            <button type="button" onClick={onClose}>Cancel</button>
            <button type="submit" className="primary">Submit Suggestion</button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default Heroes;

